import { useState } from 'react';
import { useApi } from '../../utils/hooks';
import ErrorComponent from '../../components/Error';
import moment from 'moment';
import 'moment/locale/fr';
import PropTypes from 'prop-types';
import EventsCard from '../../components/Events/EventsCard';
import '../../utils/styles/Events.css';

function Events() {
    // Set the locale for Moment.js
    moment.locale('fr');

    // State for the selected filter
    const [filter, setFilter] = useState('all');

    // Call the API to fetch the data
    const { data, loading, error } = useApi(`events/`);

    // Handle loading and error states
    if (loading) return <div className="container">Loading...</div>;
    if (error) return <ErrorComponent title="Error" errorObject={error} />;

    // Filter and Group events by date
    const filteredEvents = filterEventsByType(data.results, filter);
    const groupedEvents = groupEventsByDate(filteredEvents);

    // Event handler for filter change
    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    return (
        <div className="container">
            <p className="title-3">Événements</p>

            <select
                name="events-filter"
                id="events-filter"
                onChange={handleFilterChange}
            >
                <option value="all">Tous les événements</option>
                <option value="AGE">AGE</option>
                <option value="ADC">ADC</option>
                <option value="Bunker">Bunker</option>
                <option value="CIR">CIR</option>
                <option value="AKAP">AKAP</option>
                <option value="Revues">Revues</option>
                <option value="UNamur">UNamur</option>
                <option value="CE">CE</option>
            </select>

            {Object.keys(groupedEvents).length === 0 ? (
                <p className="paragraph" style={{ marginTop: '35px' }}>
                    Aucun événement à venir
                </p>
            ) : (
                Object.keys(groupedEvents).map((date) => (
                    <EventGroup
                        key={date}
                        date={date}
                        events={groupedEvents[date]}
                    />
                ))
            )}
        </div>
    );
}

// Function to filter events by type
const filterEventsByType = (events, filter) => {
    if (filter === 'all') return events;
    return events.filter((event) => event.category === filter);
};

// Function to group events by date
const groupEventsByDate = (events) => {
    const today = moment().startOf('day');

    return events.reduce((acc, event) => {
        let eventDate = moment(event.start_date);

        // Check if the event date is before today
        if (eventDate.isBefore(today, 'day')) {
            eventDate = today.clone();
        }

        // Format the date to YYYY-MM-DD for consistent grouping
        const dateKey = eventDate.format('YYYY-MM-DD');

        if (!acc[dateKey]) {
            acc[dateKey] = [];
        }
        acc[dateKey].push(event);

        return acc;
    }, {});
};

// Subcomponent to display a group of events for a specific date
const EventGroup = ({ date, events }) => {

    const now = moment();

    // Determine the formatted date string
    const formattedDate = moment(date).isSame(moment(), 'day')
        ? "Aujourd'hui" // Display "Today" if the date is today
        : moment(date).format('dddd D MMMM'); // Format the date otherwise

    // Function to check if an event is ongoing
    const isEventOngoing = (event) => {
        const start = moment(`${event.start_date} ${event.start_time}`, 'YYYY-MM-DD HH:mm');
        const end = moment(`${event.end_date || event.start_date} ${event.end_time}`, 'YYYY-MM-DD HH:mm');
        return now.isBetween(start, end);
    }

    // Function to get the start time display
    const getStartTimeDisplay = (event) => {
        return isEventOngoing(event)
            ? 'En cours'
            : moment(event.start_time, 'HH:mm').format('HH:mm');
    };

    return (
        <div>
            <p id="events-title-date">{formattedDate}</p>
            {events.map((event) => (
                <EventsCard
                    key={event.id}
                    id={event.id}
                    name={event.name}
                    img={event.img}
                    organizer={event.organizer}
                    location={event.location}
                    start_time={getStartTimeDisplay(event)}
                />
            ))}
        </div>
    );
};

EventGroup.propTypes = {
    date: PropTypes.string.isRequired,
    events: PropTypes.array.isRequired,
};

export default Events;
